import React, { useEffect, useState } from 'react';
import s from './RefineTextBlock.module.css';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../store/store';
import IconSimplify from '../../../../shared/ui/icons/IconSimplify';
import IconWand from '../../../../shared/ui/icons/IconWand';
import IconExtend from '../../../../shared/ui/icons/IconExtend';
import { quillRef } from '../../../../store/ref/quillRef';
import {
    selectCurrentProject,
    selectIsRefining,
    selectRefinedText,
} from '../../../../store/selectors/projectSelectors';
import { editPost, refineText } from '../../../../shared/api/textProjectApi';
import { setRefinedText } from '../../../../store/slices/projectSlice';
import Button from '../../../../shared/ui/button/Button';
import { selectIsBasicSubscription } from '../../../../store/selectors/subscriptionSelector';
import { selectIsFreeSubscription } from '../../../../store/selectors/subscriptionSelector';
import IconLock from '../../../../shared/ui/icons/IconLock';
import { setPricingModal } from '../../../../store/slices/pageSlice';

const refineOptions = [
    {
        label: 'Rephrase',
        icon: <IconWand />,
        action: 'reword',
    },
    {
        label: 'Expand',
        icon: <IconExtend />,
        action: 'long',
    },
    {
        label: 'Simplify',
        icon: <IconSimplify />,
        action: 'short',
    },
];

const RefineTextBlock: React.FC = () => {
    const currentProject = useSelector(selectCurrentProject);
    const improvedText = useSelector(selectRefinedText);
    const isLoading = useSelector(selectIsRefining);
    const dispatch = useAppDispatch();
    const [selectedText, setSelectedText] = useState('');
    const [hasSelection, setHasSelection] = useState(false);

    const isFreeSubscription = useSelector(selectIsFreeSubscription);
    const isBasicSubscription = useSelector(selectIsBasicSubscription);

    useEffect(() => {
        if (!quillRef?.current) return;

        const quill = quillRef.current.getEditor();

        const handleSelectionChange = (range: any) => {
            if (range && range.length > 0) {
                const text = quill.getText(range.index, range.length).trim();
                if (text) {
                    setSelectedText(text);
                    setHasSelection(true);
                }
            } else {
                setHasSelection(false);
                setSelectedText('');
            }
        };

        quill.on('selection-change', handleSelectionChange);

        return () => {
            quill.off('selection-change', handleSelectionChange);
        };
    }, []);

    const handleRefine = async (action: string, input: string) => {
        if (isFreeSubscription || isBasicSubscription) {
            dispatch(setPricingModal(true));
        } else {
            if (!currentProject?.id || !input) return;
            await dispatch(refineText({ input, action }));
        }
    };

    const handleApply = async () => {
        if (!improvedText || !quillRef?.current) return;

        const quill = quillRef.current.getEditor();

        if (hasSelection) {
            const range = quill.getSelection(true);
            if (range) {
                const currentFormats = quill.getFormat(range.index, 0);
                quill.deleteText(range.index, range.length, 'user');
                quill.insertText(range.index, improvedText, currentFormats, 'user');
                quill.setSelection(range.index, improvedText.length, 'silent');
            }
        } else {
            quill.setText('');
            quill.insertText(0, improvedText, {}, 'user');
            quill.setSelection(0, improvedText.length, 'silent');
        }

        dispatch(setRefinedText(''));
        setSelectedText('');
    };

    useEffect(() => {
        if (improvedText) setSelectedText(improvedText);
    }, [improvedText]);

    const handleSingleRefine = async () => {
        if (isFreeSubscription || isBasicSubscription) {
            dispatch(setPricingModal(true));
        } else {
            if (!quillRef?.current) return;
            const quill = quillRef.current.getEditor();
            const input = quill.getText().trim();
            if (!input) return;

            const action = selectedText || '';
            await dispatch(editPost({ action, input }));
        }
    };

    return (
        <div className={s.container}>
            <h3 className={s.title}>Refine Text</h3>
            <p className={s.subtitle}>
                {hasSelection
                    ? 'Highlighted text will be refined using one of the actions.'
                    : 'No text selected — enter your refinement prompt below.'}
            </p>

            <textarea
                className={s.textarea}
                placeholder={
                    hasSelection
                        ? 'Selected text will appear here...'
                        : 'Enter instructions to refine the whole document...'
                }
                readOnly={isLoading}
                value={selectedText}
                onChange={(e) => setSelectedText(e.target.value)}
            />

            <div className={s.refineOptions}>
                {hasSelection ? (
                    refineOptions.map((option) => (
                        <button
                            key={option.action}
                            className={s.refineBtn}
                            title={option.label}
                            disabled={!selectedText || isLoading}
                            onClick={() => handleRefine(option.action, selectedText)}
                        >
                            {isFreeSubscription || isBasicSubscription ? (
                                <IconLock color="#E5E7EB" />
                            ) : (
                                option.icon
                            )}
                            {option.label}
                        </button>
                    ))
                ) : (
                    <button
                        className={s.refineBtn}
                        disabled={isLoading}
                        onClick={handleSingleRefine}
                    >
                        {isFreeSubscription || isBasicSubscription ? (
                            <IconLock color="#E5E7EB" />
                        ) : (
                            <IconWand />
                        )}
                        Refine
                    </button>
                )}
            </div>

            {improvedText && !isLoading && (
                <Button
                    onClick={handleApply}
                    state="enabled"
                    text="Apply changes"
                    style={{ background: '#FFDD35', marginTop: '20px' }}
                />
            )}
        </div>
    );
};

export default RefineTextBlock;
